import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';

export default function DuplicatePairCard({ pair, selected, processing, disabled, onReview, onIgnore }) {
    const { person1, person2, reasons } = pair;
    const pairID = `${person1.id}-${person2.id}`;

    return (
        <View style={styles.card(selected)} testID={`duplicate-pair-${pairID}`}>
            <Pressable
                testID={`duplicate-review-${pairID}`}
                accessibilityRole="button"
                accessibilityLabel={`Review ${person1.firstName} ${person1.lastName} and ${person2.firstName} ${person2.lastName}`}
                accessibilityState={{ selected, disabled: processing }}
                disabled={processing}
                onPress={onReview}
                style={styles.review}
            >
                <View style={styles.people}>
                    {[person1, person2].map(person => (
                        <View key={person.id} style={styles.person}>
                            <Avatar profile={person} size={40} />
                            <View style={styles.personInfo}>
                                <Text style={styles.name} numberOfLines={1}>{person.firstName} {person.lastName}</Text>
                                <Text style={styles.email} numberOfLines={1}>{person.email || 'No email address'}</Text>
                            </View>
                        </View>
                    ))}
                </View>
                <View style={styles.reviewFooter}>
                    <View style={styles.reasons}>
                        {reasons.map(reason => (
                            <View key={reason} style={styles.reason}>
                                <Text style={styles.reasonText}>{reason}</Text>
                            </View>
                        ))}
                    </View>
                    <View style={styles.reviewAction}>
                        <Text style={styles.reviewText}>{selected ? 'Selected' : 'Review pair'}</Text>
                        <Unicon name={selected ? 'checkmark-circle' : 'arrow-forward'} size={16} colorVariant="people" />
                    </View>
                </View>
            </Pressable>
            <View style={styles.actions}>
                <Text style={styles.hint}>Different people?</Text>
                <SolidButton
                    testID={`duplicate-ignore-${pairID}`}
                    text="Ignore"
                    iconName="eye-off-outline"
                    iconBefore
                    variant="muted"
                    type="secondary"
                    size="sm"
                    disabled={disabled}
                    processing={processing}
                    accessibilityLabel={`Ignore duplicate suggestion for ${person1.firstName} ${person1.lastName} and ${person2.firstName} ${person2.lastName}`}
                    onPress={onIgnore}
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows }) => ({
    card: selected => ({
        backgroundColor: colors.surface,
        borderRadius: 8,
        boxShadow: selected ? `0 0 0 2px ${colors.people}` : shadows.card,
        overflow: 'hidden',
    }),
    review: {
        padding: spacing.md,
        gap: spacing.md,
        _web: {
            cursor: 'pointer',
            transition: 'background-color 150ms ease',
            _hover: { backgroundColor: colors.surfaceVariant },
            _focusVisible: { outlineColor: colors.people, outlineWidth: 2, outlineStyle: 'solid', outlineOffset: -2 },
        },
    },
    people: { flexDirection: { xs: 'column', md: 'row' }, gap: spacing.md },
    person: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    personInfo: { flex: 1, minWidth: 0, gap: spacing.xs },
    name: { fontFamily: fonts.bold, fontSize: typography.body, color: colors.text },
    email: { fontFamily: fonts.body, fontSize: typography.caption, color: colors.textSecondary },
    reviewFooter: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: spacing.sm },
    reasons: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
    reason: { backgroundColor: colors.peopleContainer, borderRadius: 6, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs },
    reasonText: { fontFamily: fonts.body, fontSize: typography.micro, color: colors.people },
    reviewAction: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, minHeight: 28 },
    reviewText: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.people },
    actions: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
        borderTopWidth: 1, borderTopColor: colors.borderSubtle,
    },
    hint: { fontFamily: fonts.body, fontSize: typography.caption, color: colors.textTertiary },
}));
